import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useNotifications } from '../notifications/NotificationProvider';
import {
  BellIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const NotificationList = ({ showAll = false, maxItems = 5 }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();
  const [filter, setFilter] = useState('all'); // 'all', 'unread'

  // Fetch notifications
  const { data: notifications, isLoading } = useQuery(['notifications', filter], () => {
    const params = new URLSearchParams();
    if (filter === 'unread') {
      params.append('unread_only', 'true');
    }
    if (!showAll) {
      params.append('limit', maxItems.toString());
    }
    return api.get(`/notifications?${params.toString()}`).then((res) => res.data);
  });

  // Fetch unread count
  const { data: unreadCount } = useQuery(['notifications', 'unread-count'], () =>
    api.get('/notifications/unread/count').then((res) => res.data.unread_count)
  );

  // Mark as read mutation
  const markAsReadMutation = useMutation(
    (notificationId) => api.put(`/notifications/${notificationId}/read`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notifications', 'unread-count']);
      },
      onError: (error) => {
        showError(error.response?.data?.detail || 'Failed to mark notification as read');
      },
    }
  );

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation(() => api.put('/notifications/mark-all-read'), {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
      showSuccess('All notifications marked as read');
    },
    onError: (error) => {
      showError(error.response?.data?.detail || 'Failed to mark all notifications as read');
    },
  });

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'availability_reminder':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'session_scheduled':
        return <CalendarIcon className="h-5 w-5 text-green-500" />;
      case 'session_rescheduled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'schedule_reminder':
        return <BellIcon className="h-5 w-5 text-purple-500" />;
      case 'custom_notification':
        return <UserGroupIcon className="h-5 w-5 text-indigo-500" />;
      case 'admin_announcement':
        return <InformationCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500';
      case 'high':
        return 'border-l-yellow-500';
      case 'normal':
        return 'border-l-blue-500';
      case 'low':
        return 'border-l-gray-500';
      default:
        return 'border-l-blue-500';
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if not already read
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }

    // Navigate to action URL if available
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white shadow-md">
      <div className="border-b border-gray-200 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                {unreadCount}
              </span>
            )}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            {showAll && (
              <div className="flex rounded-md shadow-sm">
                <button
                  onClick={() => setFilter('all')}
                  className={`rounded-l-md border px-3 py-2 text-sm font-medium ${
                    filter === 'all'
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={`rounded-r-md border-b border-r border-t px-3 py-2 text-sm font-medium ${
                    filter === 'unread'
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Unread
                </button>
              </div>
            )}
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsReadMutation.mutate()}
                disabled={markAllAsReadMutation.isLoading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CheckIcon className="mr-2 h-4 w-4" />
                Mark All Read
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {notifications?.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <BellIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
            <p>No notifications found</p>
          </div>
        ) : (
          notifications?.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`cursor-pointer border-l-4 p-4 transition-colors duration-150 hover:bg-gray-50 ${getPriorityColor(
                notification.priority
              )} ${!notification.is_read ? 'bg-blue-50' : ''}`}
            >
              <div className="flex items-start">
                <div className="mr-3 flex-shrink-0">
                  {getNotificationIcon(notification.notification_type)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3
                      className={`break-words text-sm font-medium ${
                        !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      {notification.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {format(new Date(notification.created_at), 'MMM d, HH:mm')}
                      </span>
                      {!notification.is_read && (
                        <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                  </div>
                  <p
                    className={`mt-1 break-words text-sm ${
                      !notification.is_read ? 'text-gray-800' : 'text-gray-600'
                    }`}
                  >
                    {notification.message}
                  </p>
                  {notification.priority === 'urgent' && (
                    <div className="mt-2">
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                        Urgent
                      </span>
                    </div>
                  )}
                  {notification.priority === 'high' && (
                    <div className="mt-2">
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        High Priority
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {!showAll && notifications?.length > 0 && (
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={() => navigate('/notifications')}
            className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            View All Notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationList;
